import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs/RX';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';
import { SelectItem } from 'primeng/components/common/selectitem';

import {
    IGeneralItem,
    ICamera,
    ILocation,
    IUser,
    IWowzaChannel,
    IUserGroup,
    ICameraMap,
    ISelectItem,
    IEvent,
    INotificationType,
    ICameraForMap,
    ICamerasForList,
    IWowzaServer,
    INewAccount,
    IWowzaServerSyncStatus,
    ICamerasForConfig,
    IWaterWeatherStations,
    ICameraList,
    IWowzaAppTemplate,
    ICameraFiltersForConfig,
    IRole,
    IUserLogins,
    ICameraTour,
    ICameraTourForList,
    ILoginRequestHistory,
    IDateTimeInterval,
    IReplication,
    IUserGroupCameraBlockage,
    IVdsManager
} from '../models/index';

import { environment } from '../../../environments/environment';
import { AuthenticationService } from './authentication.service';
import { IWall, INativeApp, IWallTour, IWallTourForAdd } from '../models/wall';

@Injectable()
export class DatacontextService {
    constructor(
        private http: HttpClient,
        private authService: AuthenticationService) { }

    private apiUrl = environment.api_url;
    private getHttpOptions() {
        return this.authService.getAuthHttpOptions();
    }

    convertToGeneralItem(item: any): IGeneralItem {
        const itemForList = {
            id: item.id,
            name: item.name,
            description: item.description
        } as IGeneralItem;
        return itemForList;
    }

    // agencies
    addAgency(agency: any) {
        return this.http.post<IGeneralItem>(this.apiUrl + 'Agencies/AddAgency', agency, this.getHttpOptions());
    }

    deleteAgency(id: number) {
        return this.http.delete(this.apiUrl + 'Agencies/DeleteAgency/' + id, this.getHttpOptions());
    }

    editAgency(agency: any) {
        return this.http.put(this.apiUrl + 'Agencies/' + agency.id, agency, this.getHttpOptions());
    }

    getAgencies(): Observable<IGeneralItem[]> {
        return this.http.get<IGeneralItem[]>(this.apiUrl + 'Agencies', this.getHttpOptions())
            .map(x => {
                return x.sort((y, z) => y.name.toLowerCase() >= z.name.toLowerCase() ? 1 : -1);
            });
    }

    getAgencyById(id: number): Observable<IGeneralItem> {
        return this.http.get<IGeneralItem>(this.apiUrl + 'Agencies/GetById/' + id, this.getHttpOptions());
    }

    // native apps - external walls
    getNativeApps(): Observable<INativeApp[]> {
        return this.http.get<INativeApp[]>(this.apiUrl + 'NativeApp/GetNativeAppsList', this.getHttpOptions());
    }

    getVideoWalls(): Observable<IWall[]> {
        return this.http.get<IWall[]>(this.apiUrl + 'NativeApp/GetVideoWallsList', this.getHttpOptions());
    }

    setExternalWall(nativeApp: INativeApp) {
        return this.http.put(this.apiUrl + 'NativeApp/', nativeApp, this.getHttpOptions());
    }

    deleteExternalWall(nativeApp: INativeApp) {
        return this.http.post(this.apiUrl + 'NativeApp/Delete/', nativeApp, this.getHttpOptions());
    }

    // video walls - internal UI walls
    getWalls(): Observable<IWall[]> {
        return this.http.get<IWall[]>(this.apiUrl + 'Walls', this.getHttpOptions())
            .map(data => {
                data.forEach(x => {
                    x.agencies = Array.from(new Set(x.cameras.map(c => c.agency))).sort().join(', ');
                });
                return data;
            });
    }

    getWallById(id: number): Observable<IWall> {
        return this.http.get<IWall>(this.apiUrl + 'Walls/GetById/' + id, this.getHttpOptions());
    }

    deleteWall(id: number) {
        return this.http.delete(this.apiUrl + 'Walls/DeleteCameraVvw/' + id, this.getHttpOptions());
    }

    editWall(wall: IWall) {return this.http.put(this.apiUrl + 'Walls/' + wall.id, wall, this.getHttpOptions());
    }

    addWall(wall: IWall) {
        return this.http.post(this.apiUrl + 'Walls/AddWall', wall, this.getHttpOptions());
    }

    getWallNames(): Observable<string[]> {
        return this.http.get<string[]>(this.apiUrl + 'Walls/GetWallNames', this.getHttpOptions());
    }

    // cameras
    addCamera(camera: ICamera) {
        return this.http.post(this.apiUrl + 'Cameras/AddCamera', camera, this.getHttpOptions());
    }

    deleteCamera(id: number) {
        return this.http.delete(this.apiUrl + 'Cameras/DeleteCamera/' + id, this.getHttpOptions());
    }

    editCamera(camera: ICamera) {
        return this.http.put(this.apiUrl + 'Cameras/' + camera.id, camera, this.getHttpOptions());
    }

    getCameras(): Observable<ICamera[]> {
        return this.http.get<ICamera[]>(this.apiUrl + 'Cameras', this.getHttpOptions());
    }

    getCameraById(id: number): Observable<ICamera> {
        return this.http.get<ICamera>(this.apiUrl + 'Cameras/' + id, this.getHttpOptions());
    }

    getCameraForConfig(id: number): Observable<ICameraList> {
        return this.http.get<ICameraList>(this.apiUrl + 'Cameras/GetForConfig' + id, this.getHttpOptions());
    }

    getCamerasForConfig(): Observable<ICamerasForConfig> {
        return this.http.get<ICamerasForConfig>(this.apiUrl + 'Cameras/GetForConfig', this.getHttpOptions());
    }

    getCameraFiltersForConfig(): Observable<ICameraFiltersForConfig> {
        return this.http.get<ICameraFiltersForConfig>(this.apiUrl + 'Cameras/GetFiltersForConfig', this.getHttpOptions());
    }

    getCamerasLiteMap(): Observable<ICameraForMap[]> {
        return this.http.get<ICameraForMap[]>(this.apiUrl + 'Cameras/GetForMap', this.getHttpOptions());
    }

    getMobileCamerasLiteMap(): Observable<ICameraForMap[]> {
        return this.http.get<ICameraForMap[]>(this.apiUrl + 'Cameras/GetMobileForMap', this.getHttpOptions());
    }


    getCamerasForList(): Observable<ICamerasForList> {
        return this.http.get<ICamerasForList>(this.apiUrl + 'Cameras/GetForList', this.getHttpOptions());
    }

    getCameraForList(id: number): Observable<ICamerasForList> {
        return this.http.get<ICamerasForList>(this.apiUrl + `Cameras/GetForList/${id}`, this.getHttpOptions());
    }

    getCameraForMap(id: number): Observable<ICameraMap> {
        return this.http.get<ICameraMap>(this.apiUrl + `Cameras/GetCameraMap/${id}`, this.getHttpOptions());
    }

    // new method - one camera
    getCameraWithSecureUrl(id: number): Observable<ICameraMap> {
        return this.http.get<ICameraMap>(this.apiUrl + `Cameras/GetCameraMapWithSecureUrl/${id}`, this.getHttpOptions());
    }

    getAgenciesForBlockingCameras(): Observable<number[]> {
        return this.http.get<number[]>(this.apiUrl + `Cameras/GetUsersAgencies`, this.getHttpOptions());
    }

    // new method - multiple
    getCamerasSecureTokenExternalVideo(cameras: any[]): Observable<any> {
        let cameraIds = cameras.map(x => x.id);
        return this.http.post<any>(this.apiUrl + `Cameras/GetSecureTokenExternalVideoPaths`, cameraIds, this.getHttpOptions())
            .map(data => {
                cameras.forEach((camera) => {
                    const updatedCamera = data.find(x => x.cameraId == camera.id);
                    if (updatedCamera) {
                        (!updatedCamera.errorMsg)
                        {
                          if(camera.wowzaExternalUrls.playerVideoFormat === 'video/mp4')
                               camera.wowzaExternalUrls.externalMP4Uri = updatedCamera.secureUri;
                          else if(camera.wowzaExternalUrls.playerVideoFormat === 'video/webm')
                               camera.wowzaExternalUrls.externalWebMUri = updatedCamera.secureUri;
                          else
                            camera.wowzaExternalUrls.externalMrlHlsUri = updatedCamera.secureUri;
                        }
                    }
                });
                return cameras;
            });
    }

    // import cameras
    bulkImportCameras(formData: any, filename: string, userName: string): Observable<any> {
        return this.http.post<any>(this.apiUrl + 'Import/PostFile?fileName=' + filename + '&userName=' + userName, formData, this.getHttpOptions());
    }

    // export cameras
    exportCameras(fileName: string, centerIds): Observable<any> {
        const options: any = {
            headers: this.getHttpOptions().headers,
            observe: 'body',
            responseType: 'arraybuffer'
        };
        return this.http.post<any>(this.apiUrl + 'Import/ExportCamerasTemplate?fileName=' + fileName, centerIds, options);
    }

    // export cameras
    exportCamerasConfig(fileName: string, centerIds, excelOrCsv: string): Observable<any> {
        const options: any = {
            headers: this.getHttpOptions().headers,
            observe: 'body',
            responseType: 'arraybuffer',
        };
        options.headers = options.headers.append('excelOrCsv', excelOrCsv);
        return this.http.post<any>(this.apiUrl + 'Import/ExportCamerasConfigs?fileName=' + fileName, centerIds, options);
    }

    updateCameraBlockage(blockage: any) {
        return this.http.post<any>(this.apiUrl + `Cameras/UpdateCameraBlockage`, blockage, this.getHttpOptions());
    }

    // centers
    addCenter(center: IGeneralItem) {
        return this.http.post<IGeneralItem>(this.apiUrl + 'Centers/AddCenter', center, this.getHttpOptions());
    }

    deleteCenter(id: number) {
        return this.http.delete(this.apiUrl + 'Centers/DeleteCenter/' + id, this.getHttpOptions());
    }

    editCenter(center: IGeneralItem) {
        return this.http.put(this.apiUrl + 'Centers/' + center.id, center, this.getHttpOptions());
    }

    getCenters(): Observable<IGeneralItem[]> {
        return this.http.get<IGeneralItem[]>(this.apiUrl + 'Centers', this.getHttpOptions())
            .map(x => {
                return x.sort((y, z) => y.name.toLowerCase() >= z.name.toLowerCase() ? 1 : -1);
            });
    }

    getCentersForEvents(): Observable<IGeneralItem[]> {
        return this.http.get<IGeneralItem[]>(this.apiUrl + 'Centers/ForEvents', this.getHttpOptions())
            .map(x => {
                return x.sort((y, z) => y.name.toLowerCase() >= z.name.toLowerCase() ? 1 : -1);
            });
    }

    getCenterById(id): Observable<IGeneralItem> {
        return this.http.get(this.apiUrl + 'Centers/' + id, this.getHttpOptions())
            .map((newItem: any) => this.convertToGeneralItem(newItem));
    }

    // counties
    addCounty(county: IGeneralItem) {
        return this.http.post<IGeneralItem>(this.apiUrl + 'Counties/AddCounty', county, this.getHttpOptions());
    }

    deleteCounty(id: number) {
        return this.http.delete(this.apiUrl + 'Counties/DeleteCounty/' + id, this.getHttpOptions());
    }

    editCounty(county: IGeneralItem) {
        return this.http.put(this.apiUrl + 'Counties/' + county.id, county, this.getHttpOptions());
    }

    getCounties(): Observable<IGeneralItem[]> {
        return this.http.get<IGeneralItem[]>(this.apiUrl + 'Counties', this.getHttpOptions())
            .map(x => {
                return x.sort((y, z) => y.name.toLowerCase() >= z.name.toLowerCase() ? 1 : -1);
            });
    }

    getCountyById(id: number): Observable<IGeneralItem> {
        return this.http.get<IGeneralItem>(this.apiUrl + 'Counties/' + id, this.getHttpOptions());
    }

    // highways
    addHighway(highway: IGeneralItem) {
        return this.http.post<IGeneralItem>(this.apiUrl + 'Highways/AddHighway', highway, this.getHttpOptions());
    }

    deleteHighway(id: number) {
        return this.http.delete(this.apiUrl + 'Highways/DeleteHighway/' + id, this.getHttpOptions());
    }

    editHighway(highway: IGeneralItem) {
        return this.http.put(this.apiUrl + 'Highways/' + highway.id, highway, this.getHttpOptions());
    }

    getHighways(): Observable<IGeneralItem[]> {
        return this.http.get<IGeneralItem[]>(this.apiUrl + 'Highways', this.getHttpOptions())
            .map(x => {
                return x.sort((y, z) => y.name.toLowerCase() >= z.name.toLowerCase() ? 1 : -1);
            });
    }

    getHighwayById(id: number): Observable<IGeneralItem> {
        return this.http.get<IGeneralItem>(this.apiUrl + 'Highways/' + id, this.getHttpOptions());
    }


     // vds managers
     addVdsManager(vdsManager: any) {
        return this.http.post<IVdsManager>(this.apiUrl + 'VdsManagers/AddVdsManager', vdsManager, this.getHttpOptions());
    }

    deleteVdsManager(id: number) {
        return this.http.delete(this.apiUrl + 'VdsManagers/DeleteVdsManager/' + id, this.getHttpOptions());
    }

    editVdsManager(vdsManager: IVdsManager) {
        return this.http.put(this.apiUrl + 'VdsManagers/' + vdsManager.id, vdsManager, this.getHttpOptions());
    }

    getVdsManagers(): Observable<IVdsManager[]> {
        return this.http.get<IVdsManager[]>(this.apiUrl + 'VdsManagers', this.getHttpOptions())
            .map(x => {
                return x.sort((y, z) => y.name.toLowerCase() >= z.name.toLowerCase() ? 1 : -1);
            });
    }

    getVdsManagerById(id: number): Observable<IVdsManager> {
        return this.http.get<IVdsManager>(this.apiUrl + 'VdsManagers/' + id, this.getHttpOptions());
    }


    // notification types
    editNotificationType(notificationType: INotificationType) {
        return this.http.put(this.apiUrl + 'NotificationExtendedProperties/' + notificationType.id, notificationType, this.getHttpOptions());
    }

    getNotificationTypes(): Observable<INotificationType[]> {
        return this.http.get<INotificationType[]>(this.apiUrl + 'NotificationExtendedProperties', this.getHttpOptions());
    }

    getNotificationTypeById(id: number): Observable<INotificationType> {
        return this.http.get<INotificationType>(this.apiUrl + 'NotificationExtendedProperties/' + id, this.getHttpOptions());
    }

    // roles
    addRole(role: any) {
        return this.http.post<IGeneralItem>(this.apiUrl + 'Roles/AddRole', role, this.getHttpOptions());
    }

    deleteRole(id: number) {
        return this.http.delete(this.apiUrl + 'Roles/DeleteRole/' + id, this.getHttpOptions());
    }

    editRole(role: any) {
        return this.http.put(this.apiUrl + 'Roles/' + role.id, role, this.getHttpOptions());
    }

    getRoles(): Observable<IRole[]> {
        return this.http.get<IRole[]>(this.apiUrl + 'Roles', this.getHttpOptions());
    }

    getAllAccessPrivileges(): Observable<ISelectItem[]> {
        return this.http.get<ISelectItem[]>(this.apiUrl + 'Roles/GetAllAccessPrivileges/', this.getHttpOptions());
    }

    getRoleById(id: number): Observable<IGeneralItem> {
        return this.http.get<IGeneralItem>(this.apiUrl + 'Roles/' + id, this.getHttpOptions());
    }

    // users
    addUser(user: any) {
        return this.http.post<IUser>(this.apiUrl + 'Users/AddUser', user, this.getHttpOptions());
    }

    deleteUser(id: number) {
        return this.http.delete(this.apiUrl + 'Users/DeleteUser/' + id, this.getHttpOptions());
    }

    editUser(user: any) {
        return this.http.put(this.apiUrl + 'Users/' + user.id, user, this.getHttpOptions());
    }

    updatePassword(user: any) {
        return this.http.put(this.apiUrl + 'Users/UpdatePassword ', user, this.getHttpOptions());
    }

    
    getUsers(): Observable<IUser[]> {
        return this.http.get<IUser[]>(this.apiUrl + 'Users', this.getHttpOptions())
            .map(x => {
                return x.sort((y, z) => y.name.toLowerCase() >= z.name.toLowerCase() ? 1 : -1);
            });
    }

    getUserLogins(): Observable<IUserLogins[]> {
        return this.http.get<IUserLogins[]>(this.apiUrl + 'Users/GetUserLogins', this.getHttpOptions())
            .map(x => {
                return x.sort((y, z) => y.name.toLowerCase() >= z.name.toLowerCase() ? 1 : -1);
            });
    }

    getLoginRequestHistory(dateTimeInterval: IDateTimeInterval): Observable<ILoginRequestHistory[]> {
        return this.http.post<ILoginRequestHistory[]>(this.apiUrl + 'Users/GetRequesterLoginInfo', dateTimeInterval, this.getHttpOptions());
    }

    getUsernames(): Observable<string[]> {
        return this.http.get<string[]>(this.apiUrl + 'Users/GetUsernames', this.getHttpOptions());
    }

    getUserById(id: number): Observable<IUser> {
        return this.http.get<IUser>(this.apiUrl + 'Users/' + id, this.getHttpOptions());
    }

    resetUserPassword(user: IUser) {
        return this.http.post(this.apiUrl + 'ResetPassword', user, this.getHttpOptions());
    }

    getResetPasswordEnabled() {
        return this.http.get<boolean>(this.apiUrl + 'ResetPassword/Enabled', this.getHttpOptions());
    }

    getUserPrivileges() : Observable<ISelectItem[]> {
        return this.http.get<ISelectItem[]>(this.apiUrl + 'Users/GetUserPrivileges', this.getHttpOptions());
    }

    // userGroups
    addUserGroup(user: any) {
        return this.http.post<IUserGroup>(this.apiUrl + 'UserGroups/AddUserGroup', user, this.getHttpOptions());
    }

    deleteUserGroup(id: number) {
        return this.http.delete(this.apiUrl + 'UserGroups/DeleteUserGroup/' + id, this.getHttpOptions());
    }

    editUserGroup(userGroup: any) {
        return this.http.put(this.apiUrl + 'UserGroups/' + userGroup.id, userGroup, this.getHttpOptions());
    }

    getUserGroups(): Observable<IUserGroup[]> {
        return this.http.get<IUserGroup[]>(this.apiUrl + 'UserGroups', this.getHttpOptions());
    }

    getUserGroupsSimple(): Observable<IUserGroup[]> {
        return this.http.get<IUserGroup[]>(this.apiUrl + 'WallTours/GetUserGroupsSimple', this.getHttpOptions());
    }

    getUserGroupById(id: number): Observable<IUserGroup> {
        return this.http.get<IUserGroup>(this.apiUrl + 'UserGroups/' + id, this.getHttpOptions());
    }

    updateUserGroupCameraBlockage(blockage: IUserGroupCameraBlockage) {
        return this.http.post(this.apiUrl + `UserGroups/UpdateCameraBlockage`, blockage, this.getHttpOptions());
    }

    // wowza app templates
    addWowzaAppTemplate(wowzaAppTemplate: any) {
        return this.http.post<IWowzaAppTemplate>(this.apiUrl + 'WowzaAppTemplates/AddWowzaAppTemplate', wowzaAppTemplate, this.getHttpOptions());
    }

    deleteWowzaAppTemplate(id: number) {
        return this.http.delete(this.apiUrl + 'WowzaAppTemplates/DeleteWowzaAppTemplate/' + id, this.getHttpOptions());
    }

    editWowzaAppTemplate(wowzaAppTemplate: any) {
        return this.http.put(this.apiUrl + 'WowzaAppTemplates/' + wowzaAppTemplate.id, wowzaAppTemplate, this.getHttpOptions());
    }

    getWowzaAppTemplates(): Observable<IWowzaAppTemplate[]> {
        return this.http.get<IWowzaAppTemplate[]>(this.apiUrl + 'WowzaAppTemplates', this.getHttpOptions());
    }

    getWowzaAppTemplateById(id: number): Observable<IWowzaAppTemplate> {
        return this.http.get<IWowzaAppTemplate>(this.apiUrl + 'WowzaAppTemplates/' + id, this.getHttpOptions());
    }

    // wowza app servers
    addWowzaServer(wowzaServer: any) {
        return this.http.post<IWowzaServer>(this.apiUrl + 'WowzaServers/AddWowzaServer', wowzaServer, this.getHttpOptions());
    }

    deleteWowzaServer(id: number) {
        return this.http.delete(this.apiUrl + 'WowzaServers/DeleteWowzaServer/' + id, this.getHttpOptions());
    }

    editWowzaServer(wowzaServer: any) {
        return this.http.put(this.apiUrl + 'WowzaServers/' + wowzaServer.id, wowzaServer, this.getHttpOptions());
    }

    getWowzaServers(): Observable<IWowzaServer[]> {
        return this.http.get<IWowzaServer[]>(this.apiUrl + 'WowzaServers', this.getHttpOptions());
    }

    getWowzaServerById(id: number): Observable<IWowzaServer> {
        return this.http.get<IWowzaServer>(this.apiUrl + 'WowzaServers/' + id, this.getHttpOptions());
    }

    syncWowzaServerById(id: number): Observable<IWowzaServerSyncStatus> {
        return this.http.get<IWowzaServerSyncStatus>(this.apiUrl + 'WowzaServers/SyncWowzaServer/' + id, this.getHttpOptions());
    }

    getWowzaServerStatusById(id: number): Observable<IWowzaServerSyncStatus> {
        return this.http.get<IWowzaServerSyncStatus>(this.apiUrl + 'WowzaServers/GetWowzaServerSyncStatus/' + id, this.getHttpOptions());
    }

    // wowza channels
    addWowzaChannel(wowzaChannel: any) {
        return this.http.post<IWowzaChannel>(this.apiUrl + 'WowzaChannels/AddWowzaChannel', wowzaChannel, this.getHttpOptions());
    }

    deleteWowzaChannel(id) {
        return this.http.delete(this.apiUrl + 'WowzaChannels/DeleteWowzaChannel/' + id, this.getHttpOptions());
    }

    editWowzaChannel(wowzaChannel: any) {
        return this.http.put(this.apiUrl + 'WowzaChannels/' + wowzaChannel.id, wowzaChannel, this.getHttpOptions());
    }

    getWowzaChannels(): Observable<IWowzaChannel[]> {
        return this.http.get(this.apiUrl + 'WowzaChannels', this.getHttpOptions())
            .map((response: any) => response.map(wowzaChannel => this.convertToWowzaChannelList(wowzaChannel)) as IWowzaChannel[]);
    }

    getWowzaChannelById(id: number): Observable<IWowzaChannel> {
        return this.http.get(this.apiUrl + 'WowzaChannels/' + id, this.getHttpOptions())
            .map((newItem: any) => this.convertToWowzaChannelList(newItem));
    }

    getEventsWithCameras(): Observable<IEvent[]> {
        return this.http.get<IEvent[]>(this.apiUrl + 'Events/GetEventsAndCamerasByNetwork', this.getHttpOptions());
    }

    // New Account Request
    addNewAccount(newAccount: any) {
        return this.http.post<IEvent>(this.apiUrl + 'NewAccount/Add', newAccount, this.getHttpOptions());
    }

    editAccountRequest(newAccount: INewAccount) {
        return this.http.put<IEvent>(this.apiUrl + 'NewAccount/Edit', newAccount, this.getHttpOptions());
    }

    revertToPending(newAccount: any): Observable<INewAccount[]> {
        return this.http.put<INewAccount[]>(this.apiUrl + 'NewAccount/RevertToPending', newAccount, this.getHttpOptions());
    }

    getNewAccountsList(): Observable<INewAccount[]> {
        return this.http.get<INewAccount[]>(this.apiUrl + 'NewAccount/GetNewAccounts', this.getHttpOptions());
    }

    acknowledgeCreationAccountRequest(user, requestOfNewAccountId): Observable<INewAccount[]> {
        return this.http.post<INewAccount[]>(this.apiUrl + 'NewAccount/AcknowledgeCreation/' + requestOfNewAccountId, user, this.getHttpOptions());
    }

    // start Water & Weather Stations
    getWaterStations(): Observable<IWaterWeatherStations[]> {
        return this.http.get<IWaterWeatherStations[]>(this.apiUrl + 'WaterStations/GetWaterStations', this.getHttpOptions());
    }

    getWeatherStations(): Observable<IWaterWeatherStations[]> {
        return this.http.get<IWaterWeatherStations[]>(this.apiUrl + 'WeatherStations/GetWeatherStations', this.getHttpOptions());
    }
    // end Water & Weather Stations

    // start cameras tours calls
    getCameraTours(): Observable<ICameraTourForList[]> {
        return this.http.get<ICameraTourForList[]>(this.apiUrl + 'CameraTours', this.getHttpOptions());
    }

    editCameraTour(cameraTour: ICameraTour): Observable<ICameraTourForList[]> {
        return this.http.put<ICameraTourForList[]>(this.apiUrl + 'CameraTours/UpdateCameraTour', cameraTour, this.getHttpOptions());
    }

    getCameraTourById(id: number): Observable<ICameraTour> {
        return this.http.get<ICameraTour>(this.apiUrl + 'CameraTours/GetById/' + id, this.getHttpOptions());
    }

    getAllCameraTourNames(): Observable<string[]> {
        return this.http.get<string[]>(this.apiUrl + 'CameraTours/GetAllCameraTourNames', this.getHttpOptions());
    }
    // end cameras tours calls

    // wall tour calls
    getVideoWallTours(): Observable<IWallTour[]> {
        return this.http.get<IWallTour[]>(this.apiUrl + 'WallTours', this.getHttpOptions());
    }

    getVideoWallTourById(id: number) {
        return this.http.get(this.apiUrl + 'WallTours/GetById/' + id, this.getHttpOptions());
    }

    addNewWallTour(wallTour: IWallTourForAdd) {
        return this.http.post(this.apiUrl + 'WallTours/AddWallTour', wallTour, this.getHttpOptions());
    }

    deleteWallTour(id: number) {
        return this.http.delete(this.apiUrl + 'WallTours/DeleteWallTour/' + id, this.getHttpOptions());
    }
    //

    // bulk import timer
    checkStatusOfBulkImport(): Observable<any> {
        const options: any = {
            headers: this.getHttpOptions().headers,
            observe: 'response',
            responseType: 'arraybuffer'
        };
        return this.http.get<any>(this.apiUrl + 'Import/CheckImportStatus', options);
    }

    // Methods
    convertToCamerasList(item: any): ICamera {
        const itemForList = {
            id: item.id,
            name: item.name,
            description: item.description,
            centerName: item.center.name,
            locationName: item.location.name,
            snapshotAddress: item.snapshotAddress,
            sourceSystemId: item.sourceSystemId,
            createdBy: item.timestamps.createdBy,
            createdDateTime: item.timestamps.createdUtc,
            modifiedBy: item.timestamps.modifiedBy,
            modifiedDateTime: item.timestamps.modifiedUtc,
        } as ICamera;
        return itemForList;
    }

    convertToLocationList(item: any): ILocation {
        const itemForList = {
            id: item.id,
            name: item.name,
            description: item.description,
            agency: item.agency.name,
            county: item.county.name,
            highway: item.highway.name,
            mileMarker: item.mileMarker,
            latitude: item.latitude,
            longitude: item.longitude,
            direction: item.direction
        } as ILocation;
        return itemForList;
    }

    convertToWowzaChannelList(item: any): IWowzaChannel {
        const itemForList = {
            id: item.id,
            name: item.name,
            description: item.description,
            internalOutputUri: item.internalOutputUri,
            externalOutputUri: item.externalOutputUri,
            state: item.state,
            wowzaServer: item.wowzaServer.name,
            sourceConfigFilePath: item.sourceConfigFilePath,
            longitude: item.longitude,
            direction: item.direction
        } as IWowzaChannel;
        return itemForList;
    }

    getItemById(items: any[], id: number): any {
        const filteredItems = items.filter(x => x['id'] === id);
        if (filteredItems.length) {
            return filteredItems[0];
        }

        return null;
    }

    getItemByKey(items: any[], id: number, key: string): any {
        const filteredItems = items.filter(x => x[key] === id);
        if (filteredItems.length) {
            return filteredItems[0];
        }
        return null;
    }

    getNameById(items: ISelectItem[], id: number): string {
        const filteredItems = items.filter(x => x.id === id);
        if (filteredItems.length) {
            return filteredItems[0].name;
        }
        return null;
    }

    getLabelById(items: SelectItem[], id: number): string {
        const filteredItems = items.filter(x => x.value === id);
        if (filteredItems.length) {
            return filteredItems[0].label;
        }
        return null;
    }

    getSelectedItems(items: ISelectItem[]): SelectItem[] {
        const options: SelectItem[] = [];
        items.forEach(item => {
            const option: SelectItem = {
                value: item.id,
                label: item.name
            };
            options.push(option);
        });
        return options;
    }

    // Replication
    getReplication(): Observable<IReplication[]> {
        return this.http.get<IReplication[]>(this.apiUrl + 'Replication/GetReplicationInfo', this.getHttpOptions());
    }
    //

    // Send test email - for testing SMTP settings
    sendTestEmail(testemail: string) {
        return this.http.post(this.apiUrl + 'Users/SendTestEmail', { emailAddress: testemail }, this.getHttpOptions());
    }
    //
}
